import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { ArrowLeft } from "lucide-react";
import { SEO, SITE_ORIGIN } from "@/components/nyla/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const articleJsonLd = post && {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${SITE_ORIGIN}/blog/${slug}#article`,
    headline: post.title,
    description: post.excerpt,
    url: `${SITE_ORIGIN}/blog/${slug}`,
    image: post.image ? [post.image] : undefined,
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    author: {
      "@type": "Person",
      name: post.author || "Nyla Editorial",
      url: `${SITE_ORIGIN}/journal`,
    },
    publisher: {
      "@type": "Organization",
      "@id": `${SITE_ORIGIN}/#organization`,
      name: "Nyla Air Water",
      url: SITE_ORIGIN,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_ORIGIN}/nyla-logo.png`,
        width: 512,
        height: 512,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_ORIGIN}/blog/${slug}`,
    },
    isPartOf: {
      "@type": "Blog",
      "@id": `${SITE_ORIGIN}/journal#blog`,
      name: "The Nyla Journal",
    },
    articleSection: post.category,
    keywords: [post.category, "Nyla Air Water", "sustainable hydration", "air water"].join(", "),
    wordCount: post.content_html
      ? post.content_html.replace(/<[^>]+>/g, " ").trim().split(/\s+/).length
      : (post.content || []).join(" ").trim().split(/\s+/).length,
    timeRequired: `PT${parseInt(post.read_time, 10) || 4}M`,
    inLanguage: "en-IN",
  };

  const breadcrumbJsonLd = post && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE_ORIGIN}/journal` },
      { "@type": "ListItem", position: 3, name: post.title, item: `${SITE_ORIGIN}/blog/${slug}` },
    ],
  };

  return (
    <main data-testid="blog-post-page" className="bg-[#F5F5F7] text-[#1D1D1F] font-body min-h-screen">
      {post && (
        <SEO
          title={post.title}
          description={post.excerpt}
          path={`/blog/${slug}`}
          image={post.image}
          type="article"
          keywords={`${post.category}, Nyla Air Water, ${post.title}`}
          article={{
            publishedTime: post.published_at,
            modifiedTime: post.updated_at || post.published_at,
            author: post.author,
            section: post.category,
          }}
          jsonLd={[articleJsonLd, breadcrumbJsonLd]}
        />
      )}
      {notFound && (
        <SEO title="Article not found" path={`/blog/${slug}`} noindex />
      )}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#F5F5F7]/70 backdrop-blur-2xl border-b border-[#D2D2D7]/60">
        <div className="max-w-3xl mx-auto px-6 md:px-12 flex items-center justify-between h-14">
          <Link to="/journal" data-testid="blog-back-link" className="flex items-center gap-2 text-sm font-medium text-[#1D1D1F] hover:text-[#2C3E35] transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" />
            Back to Journal
          </Link>
          <Link to="/" className="flex items-center">
            <img src="/nyla-logo.png" alt="Nyla Air Water" className="h-10 w-auto" />
          </Link>
        </div>
      </header>

      {notFound && (
        <div className="pt-40 pb-32 text-center px-6">
          <h1 className="font-display text-4xl font-bold tracking-tight mb-4">Article not found</h1>
          <Link to="/" className="text-[#2C3E35] font-medium">Return home →</Link>
        </div>
      )}

      {post && (
        <article className="pt-28 md:pt-36 pb-24">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#2C3E35]">{post.category}</span>
              <span className="text-xs text-[#86868B]">{post.read_time}</span>
              <span className="text-xs text-[#86868B]">{post.published_at}</span>
            </div>
            <h1 data-testid="blog-post-title" className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tighter font-bold mb-8">
              {post.title}
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#86868B] mb-10">{post.excerpt}</p>
          </div>

          {post.image && (
            <div className="max-w-5xl mx-auto px-6 md:px-12 mb-14">
              <img src={post.image} alt={post.title} className="w-full h-[320px] md:h-[480px] object-cover rounded-3xl" />
            </div>
          )}

          <div className="max-w-3xl mx-auto px-6 md:px-12 space-y-7">
            {post.content_html ? (
              <div
                data-testid="blog-post-html"
                className="article-body"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content_html) }}
              />
            ) : (
              post.content.map((para, i) => (
                <p key={i} className="text-base md:text-lg leading-relaxed text-[#1D1D1F]/80">
                  {para}
                </p>
              ))
            )}
            <div className="pt-10 border-t border-[#D2D2D7]/60">
              <p className="text-sm text-[#86868B] mb-6">Written by {post.author}</p>
              <Link
                to="/"
                data-testid="blog-post-home-cta"
                className="inline-block px-7 py-3.5 rounded-full bg-[#1D1D1F] text-white text-sm font-medium hover:bg-black active:scale-95 transition-[background-color,transform] duration-200"
              >
                Explore Nyla Air Water
              </Link>
            </div>
          </div>
        </article>
      )}

      <footer className="py-10 border-t border-[#D2D2D7]/60 text-center">
        <p className="text-xs text-[#86868B]">© {new Date().getFullYear()} Nyla Air Water · Where Nature Meets Purity</p>
      </footer>
    </main>
  );
}
